import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { did, type DidPresenter, type DidVoice } from "./client";
import type { PickerOption } from "@/lib/liveavatar/options";

const STALE = 5 * 60 * 1000;

function pickThumb(p: DidPresenter): string | undefined {
  const isImage = (u?: string) => !!u && !/\.(mp4|webm|mov)(\?|$)/i.test(u);
  return (
    p.thumbnail_url ||
    p.image_url ||
    (isImage(p.preview_url) ? p.preview_url : undefined) ||
    (isImage(p.video_url) ? p.video_url : undefined) ||
    undefined
  );
}

/** D-ID expressive avatars, mapped to the shared PickerOption shape. */
export function useDidPresenters(opts?: { enabled?: boolean }) {
  const q = useQuery({
    queryKey: ["did", "presenters"],
    queryFn: () => did.listPresenters(),
    staleTime: STALE,
    enabled: opts?.enabled ?? true,
  });
  const raw = q.data?.presenters ?? [];
  const options: PickerOption[] = raw
    .map((p): PickerOption | null => {
      const id = String(p.presenter_id || "").trim();
      if (!id) return null;
      return {
        id,
        label: String(p.name || id).trim(),
        preview_url: pickThumb(p),
        sublabel: "Voice capable",
        gender: p.gender ? String(p.gender).toLowerCase() : undefined,
      };
    })
    .filter((x): x is PickerOption => x !== null);
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}

/** Raw voice query — shared by the voice picker and language derivation. */
export function useDidVoicesRaw(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["did", "voices"],
    queryFn: () => did.listVoices(),
    staleTime: STALE,
    enabled: opts?.enabled ?? true,
  });
}

/** Primary language subtag, so `he` and `he-IL` count as the same language. */
function baseLang(locale: string): string {
  return String(locale || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
}

/**
 * Extract every locale a voice supports, paired with the language name the
 * voice row itself advertises. Both values come straight from the catalog,
 * so a locale offered here is always one this voice can actually speak.
 */
function voiceLanguageEntries(v: DidVoice): Array<{ locale: string; language: string }> {
  const list = Array.isArray(v.languages) ? v.languages : [];
  const out = new Map<string, string>();
  for (const l of list) {
    const locale = (l?.locale || l?.language || "").trim();
    if (!locale) continue;
    out.set(locale, (l?.language || locale).trim());
  }
  if (!out.size && v.language) {
    const lang = String(v.language).trim();
    if (lang) out.set(lang, lang);
  }
  return Array.from(out.entries()).map(([locale, language]) => ({ locale, language }));
}

/** Locales a voice supports. */
function voiceLocales(v: DidVoice): string[] {
  return voiceLanguageEntries(v).map((e) => e.locale);
}

/** Whether a voice can speak the requested locale (region-insensitive). */
function voiceSpeaks(v: DidVoice, locale: string): boolean {
  const wantedBase = baseLang(locale);
  if (!wantedBase) return true;
  return voiceLocales(v).some((l) => baseLang(l) === wantedBase);
}

/**
 * D-ID TTS voices, mapped to the shared PickerOption shape. Every row carries
 * its own TTS provider and its own real language list, and both travel with
 * the option, so any voice offered here forms a valid pair with the selected
 * language. When `locale` is given, only voices that speak that language are
 * returned, and the sublabel names the provider + that language.
 */
export function useDidVoices(opts?: { enabled?: boolean; locale?: string }) {
  const q = useDidVoicesRaw(opts);
  const options = useMemo<PickerOption[]>(() => {
    const raw = q.data?.voices ?? [];
    const wanted = opts?.locale?.trim() || undefined;
    return raw
      .map((v: DidVoice): PickerOption | null => {
        const id = String(v.id || "").trim();
        if (!id) return null;
        const locs = voiceLocales(v);
        if (wanted && !voiceSpeaks(v, wanted)) return null;
        const providerTag = v.provider ? String(v.provider) : undefined;
        const entries = voiceLanguageEntries(v);
        const activeLocale =
          (wanted
            ? locs.find((l) => baseLang(l) === baseLang(wanted))
            : undefined) || locs[0];
        const languageName = entries.find((e) => e.locale === activeLocale)?.language;
        // The catalog spans several TTS providers now, so name it — voice
        // quality differs noticeably between them.
        const sublabel = [languageName, providerTag].filter(Boolean).join(" · ") || undefined;
        // Grab the preview scoped to the selected locale when possible.
        const langs = Array.isArray(v.languages) ? v.languages : [];
        const preview =
          (activeLocale
            ? langs.find((l) => (l?.locale || l?.language) === activeLocale)?.previewUrl
            : undefined) ||
          langs.find((l) => l?.previewUrl)?.previewUrl;
        return {
          id,
          label: String(v.name || id).trim(),
          sublabel,
          preview_url: preview,
          gender: v.gender ? String(v.gender).toLowerCase() : undefined,
          language: activeLocale,
          provider: providerTag,
        };
      })
      .filter((x): x is PickerOption => x !== null);
  }, [q.data, opts?.locale]);
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}

/**
 * Every language the D-ID voice catalog can actually speak, deduplicated by
 * primary subtag so `he` (Cartesia) and `he-IL` (Microsoft/Google) collapse
 * into one "Hebrew" option. `id` is a real locale string from the catalog,
 * which is what the voice picker filters on, and the label is the shortest
 * language name the catalog gives that language — never a constructed one.
 *
 * When `voiceId` is set, the list narrows to that voice's own languages, so
 * picking a voice first is just as valid as picking a language first.
 */
export function useDidLocaleList(opts?: { enabled?: boolean; voiceId?: string }): {
  options: PickerOption[];
  isLoading: boolean;
  error: Error | null;
} {
  const q = useDidVoicesRaw(opts);
  const options = useMemo<PickerOption[]>(() => {
    const all = q.data?.voices ?? [];
    const picked = opts?.voiceId
      ? all.find((v) => String(v.id || "").trim() === opts.voiceId)
      : undefined;
    const raw = picked ? [picked] : all;
    const seen = new Map<string, { id: string; label: string }>();
    for (const v of raw) {
      for (const { locale, language } of voiceLanguageEntries(v)) {
        const key = baseLang(locale);
        if (!key) continue;
        const prev = seen.get(key);
        if (!prev) {
          seen.set(key, { id: locale, label: language });
          continue;
        }
        // Prefer the region-qualified locale as the id (the standard form),
        // and the shortest name as the label ("Hebrew" over "Hebrew (Israel)").
        const id = prev.id.includes("-") ? prev.id : locale.includes("-") ? locale : prev.id;
        const label = language.length < prev.label.length ? language : prev.label;
        seen.set(key, { id, label });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data, opts?.voiceId]);
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}
